import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Loader2, Send, Pencil, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FileUpload } from "./FileUpload";
import { DrawingIntake } from "./understanding/DrawingIntake";
import { authApi, formatApiErrorDetail } from "../lib/authApi";

export const RFQ_STATUS_STYLES = {
  DRAFT: "bg-slate-500/10 text-slate-500 dark:text-slate-400 border-slate-500/30",
  SUBMITTED: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
  QUOTING: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30",
  QUOTED: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30",
  APPROVED: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  REJECTED: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30",
  CLOSED: "bg-slate-500/10 text-slate-500 dark:text-slate-400 border-slate-500/30",
};

export const RfqStatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${RFQ_STATUS_STYLES[status] || RFQ_STATUS_STYLES.DRAFT}`} data-testid={`rfq-status-badge-${status?.toLowerCase()}`}>
    {status}
  </span>
);

const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const fmtINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const emptyPart = () => ({ name: "", process: "", material: "", grade: "", quantity: "", notes: "", files: [] });

const toEditableParts = (parts) =>
  parts.map((p) => ({
    ...p,
    quantity: String(p.quantity),
    files: (p.files || []).map((f) => ({ id: f.id, original_filename: f.name, size: f.size })),
  }));

const PartEditor = ({ part, index, config, onChange, onRemove, removable }) => {
  const set = (k, v) => onChange({ ...part, [k]: v });
  const mat = config?.materials.find((m) => m.name === part.material);
  const grades = mat?.grades || [];
  return (
    <div className="p-5 rounded-lg border border-border bg-muted/30 space-y-4" data-testid={`rfq-part-editor-${index}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech">Part {index + 1}</p>
        {removable && (
          <button type="button" onClick={onRemove} className="p-2 -m-1 text-muted-foreground hover:text-destructive transition-colors duration-200" data-testid={`rfq-part-remove-${index}`}>
            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Part Name *</Label>
          <Input value={part.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Mounting Bracket" data-testid={`rfq-part-name-${index}`} />
        </div>
        <div className="space-y-2">
          <Label>Quantity *</Label>
          <Input type="number" min="1" value={part.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="e.g. 100" data-testid={`rfq-part-qty-${index}`} />
        </div>
        <div className="space-y-2">
          <Label>Process *</Label>
          <Select value={part.process} onValueChange={(v) => set("process", v)}>
            <SelectTrigger data-testid={`rfq-part-process-${index}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {(config?.processes || []).map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Material *</Label>
          <Select value={part.material} onValueChange={(v) => onChange({ ...part, material: v, grade: "" })}>
            <SelectTrigger data-testid={`rfq-part-material-${index}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {(config?.materials || []).map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Grade</Label>
          {grades.length > 0 ? (
            <Select value={part.grade} onValueChange={(v) => set("grade", v)}>
              <SelectTrigger data-testid={`rfq-part-grade-${index}`}><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={part.grade} onChange={(e) => set("grade", e.target.value)} placeholder="Optional" data-testid={`rfq-part-grade-input-${index}`} />
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea rows={2} value={part.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Tolerances, finishes..." data-testid={`rfq-part-notes-${index}`} />
      </div>
      <FileUpload testId={`rfq-part-${index}-upload`} files={part.files} onChange={(files) => set("files", files)} />
    </div>
  );
};

const RfqForm = ({ config, initial, onSaved, onCancel }) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [parts, setParts] = useState(initial ? toEditableParts(initial.parts) : [emptyPart()]);
  const [saving, setSaving] = useState(null);

  const buildPayload = () => ({
    title, notes,
    parts: parts.map((p) => ({
      name: p.name, process: p.process, material: p.material, grade: p.grade || "",
      quantity: parseInt(p.quantity, 10), notes: p.notes || "", file_ids: p.files.map((f) => f.id),
    })),
  });

  const isValid = title.trim() && parts.every((p) => p.name && p.process && p.material && parseInt(p.quantity, 10) >= 1);

  const save = async (submit) => {
    if (!isValid) {
      toast.error("Fill in the RFQ title and, for every part: name, process, material and quantity.");
      return;
    }
    setSaving(submit ? "submit" : "draft");
    try {
      let data;
      if (initial?.id) {
        ({ data } = await authApi.patch(`/rfqs/${initial.id}`, buildPayload()));
        if (submit) ({ data } = await authApi.post(`/rfqs/${initial.id}/submit`));
      } else {
        ({ data } = await authApi.post("/rfqs", { ...buildPayload(), submit }));
      }
      toast.success(submit ? "RFQ submitted — our engineering team will quote it shortly." : "Draft saved.");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-8 rounded-xl bg-card border border-border space-y-6" data-testid="rfq-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold font-heading text-lg">{initial ? `Edit ${initial.rfq_number}` : "New RFQ"}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="rfq-form-cancel-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Back
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>RFQ Title *</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Enclosure assembly — Q3 batch" data-testid="rfq-title-input" />
        </div>
        <div className="space-y-2">
          <Label>Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Overall requirements, timeline..." data-testid="rfq-notes-input" />
        </div>
      </div>
      <div className="space-y-4">
        {parts.map((p, i) => (
          <PartEditor key={i} part={p} index={i} config={config}
            onChange={(np) => setParts(parts.map((x, xi) => (xi === i ? np : x)))}
            onRemove={() => setParts(parts.filter((_, xi) => xi !== i))}
            removable={parts.length > 1} />
        ))}
        <Button type="button" variant="outline" onClick={() => setParts([...parts, emptyPart()])} className="rounded-full" data-testid="rfq-add-part-btn">
          <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add Part
        </Button>
      </div>
      <div className="flex flex-wrap gap-3 pt-2 border-t border-border">
        <Button variant="outline" disabled={!!saving} onClick={() => save(false)} className="rounded-full px-6 mt-4" data-testid="rfq-save-draft-btn">
          {saving === "draft" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Pencil className="h-4 w-4 mr-2" strokeWidth={1.5} />}
          Save Draft
        </Button>
        <Button disabled={!!saving} onClick={() => save(true)} className="rounded-full px-6 mt-4" data-testid="rfq-submit-btn">
          {saving === "submit" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Send className="h-4 w-4 mr-2" strokeWidth={1.5} />}
          Submit RFQ
        </Button>
      </div>
    </div>
  );
};

const QuoteBox = ({ rfq, onDecide, acting }) => {
  const [po, setPo] = useState("");
  return (
  <div className="mt-4 p-5 rounded-lg bg-violet-500/5 border border-violet-500/20" data-testid={`rfq-quote-box-${rfq.id}`}>
    <p className="text-xs font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400 font-mono-tech mb-2">Firm Quote</p>
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
      <span className="font-mono-tech text-xl font-bold">{fmtINR(rfq.quote.price)}</span>
      <span className="text-muted-foreground">Lead time: <b>{rfq.quote.lead_time_days} days</b></span>
      <span className="text-muted-foreground">Valid until: {new Date(rfq.quote.valid_until).toLocaleDateString("en-IN", { dateStyle: "medium" })}</span>
    </div>
    {rfq.quote.admin_notes && <p className="mt-2 text-sm text-muted-foreground">{rfq.quote.admin_notes}</p>}
    {rfq.order_number && (
      <p className="mt-3 text-sm" data-testid={`rfq-order-link-${rfq.id}`}>Order <b className="font-mono-tech text-primary">{rfq.order_number}</b> created — track it in the Orders tab.</p>
    )}
    {rfq.status === "QUOTED" && (
      <div className="mt-4 space-y-3">
        <div className="max-w-xs space-y-1.5">
          <Label htmlFor={`po-${rfq.id}`} className="text-xs">Your PO number (optional)</Label>
          <Input id={`po-${rfq.id}`} value={po} onChange={(e) => setPo(e.target.value)} maxLength={100} placeholder="e.g. PO-2026-0042" data-testid={`rfq-po-input-${rfq.id}`} />
        </div>
        <div className="flex gap-3">
          <Button size="sm" disabled={!!acting} onClick={() => onDecide(rfq, "approve", po)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`rfq-approve-btn-${rfq.id}`}>
            <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Approve
          </Button>
          <Button size="sm" variant="outline" disabled={!!acting} onClick={() => onDecide(rfq, "reject")} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10" data-testid={`rfq-reject-btn-${rfq.id}`}>
            <XCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Reject
          </Button>
        </div>
      </div>
    )}
  </div>
  );
};

export const RfqSection = ({ rfqs, setRfqs, onOrderCreated }) => {
  const [config, setConfig] = useState(null);
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    authApi.get("/library/pricing-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const upsert = (rfq) => setRfqs((prev) => {
    const exists = prev.some((r) => r.id === rfq.id);
    return exists ? prev.map((r) => (r.id === rfq.id ? rfq : r)) : [rfq, ...prev];
  });

  const submitDraft = async (rfq) => {
    setActing(rfq.id);
    try {
      const { data } = await authApi.post(`/rfqs/${rfq.id}/submit`);
      upsert(data);
      toast.success("RFQ submitted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const decide = async (rfq, decision, poNumber = "") => {
    setActing(rfq.id);
    try {
      const { data } = await authApi.post(`/rfqs/${rfq.id}/decision`, { decision, po_number: poNumber });
      upsert(data);
      if (data.order_id && onOrderCreated) onOrderCreated();
      toast.success(decision === "approve" ? `Quote approved — order ${data.order_number || ""} created.` : "Quote rejected.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  if (mode === "drawing") return <DrawingIntake onCancel={() => setMode("list")} />;
  if (mode !== "list") {
    return <RfqForm config={config} initial={mode === "edit" ? editing : null}
      onSaved={(rfq) => { upsert(rfq); setMode("list"); setEditing(null); }}
      onCancel={() => { setMode("list"); setEditing(null); }} />;
  }

  return (
    <div className="space-y-4" data-testid="rfq-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Multi-part RFQs with drafts, firm quotes and approval.</p>
        <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setMode("drawing")} className="rounded-full" data-testid="rfq-new-from-drawing-btn"><FileText className="h-4 w-4 mr-2" />From drawing</Button>
        <Button onClick={() => setMode("create")} className="rounded-full" data-testid="rfq-new-btn">
          <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> New RFQ
        </Button>
        </div>
      </div>

      {rfqs.length === 0 && (
        <div className="p-10 rounded-xl bg-card border border-border text-center" data-testid="rfq-empty">
          <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-semibold font-heading">No RFQs yet</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Create your first multi-part RFQ — attach CAD files, save drafts, and get a firm engineering-reviewed quote.</p>
        </div>
      )}

      {rfqs.map((rfq) => (
        <div key={rfq.id} className="p-6 rounded-xl bg-card border border-border" data-testid={`rfq-card-${rfq.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="font-mono-tech text-sm font-bold text-primary">{rfq.rfq_number}</span>
              <h4 className="font-semibold font-heading">{rfq.title}</h4>
              <RfqStatusBadge status={rfq.status} />
            </div>
            <span className="text-xs text-muted-foreground">{fmtDate(rfq.created_at)}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {rfq.parts.map((p, i) => (
              <Badge key={i} variant="outline" className="font-normal">
                {p.name} · {p.process} · {p.material}{p.grade ? ` ${p.grade}` : ""} × {p.quantity}
                {p.files?.length ? ` · ${p.files.length} file${p.files.length > 1 ? "s" : ""}` : ""}
              </Badge>
            ))}
          </div>
          {rfq.estimate_min != null && (
            <p className="mt-3 font-mono-tech text-sm font-bold text-primary" data-testid={`rfq-estimate-${rfq.id}`}>
              Indicative: {fmtINR(rfq.estimate_min)} – {fmtINR(rfq.estimate_max)}
            </p>
          )}
          {rfq.quote && <QuoteBox rfq={rfq} onDecide={decide} acting={acting} />}
          {(rfq.product_understanding || rfq.parts.some(p => p.file_ids?.length)) && <Button asChild variant="outline" size="sm" className="mt-4 rounded-full" data-testid={`rfq-understanding-${rfq.id}`}><Link to={`/portal/rfqs/${rfq.id}/understanding`}><FileText className="h-4 w-4 mr-2" />Drawing review</Link></Button>}
          {rfq.status === "DRAFT" && !rfq.product_understanding && (
            <div className="mt-4 flex gap-3">
              <Button size="sm" variant="outline" onClick={() => { setEditing(rfq); setMode("edit"); }} className="rounded-full" data-testid={`rfq-edit-btn-${rfq.id}`}>
                <Pencil className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Edit Draft
              </Button>
              <Button size="sm" disabled={acting === rfq.id} onClick={() => submitDraft(rfq)} className="rounded-full" data-testid={`rfq-submit-draft-btn-${rfq.id}`}>
                {acting === rfq.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
                Submit
              </Button>
            </div>
          )}
          {rfq.timeline?.length > 1 && (
            <div className="mt-4 pt-3 border-t border-border flex flex-wrap gap-x-4 gap-y-1">
              {rfq.timeline.map((t, i) => (
                <span key={i} className="text-xs text-muted-foreground">
                  <b>{t.status}</b> · {fmtDate(t.ts)}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
